import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as scheduleApi from "../../api/schedule";
import * as dashboardApi from "../../api/dashboard";
import * as interventionsApi from "../../api/interventions";
import { STATO_ORDER } from "../../utils/statoColors";
import { useCountUp } from "../../hooks/useCountUp";
import { RiepilogoModuli } from "../../components/ui/RiepilogoModuli";
import { AndamentoModuli } from "../../components/ui/AndamentoModuli";
import { DonutPerStato } from "../../components/ui/DonutPerStato";
import { MappaWinkOnCash } from "../../components/ui/MappaWinkOnCash";

// Palette generica per il tipo dispositivo (nessuna semantica come per lo
// stato: sono solo 5 colori distinguibili, riusano la stessa tavolozza).
const TIPO_COLORS = ["#18273d", "#f3b149", "#87908b", "#22c55e", "#6b7280"];

function toChartData(record: Record<string, number>): { name: string; value: number }[] {
  return Object.entries(record).map(([name, value]) => ({ name, value }));
}

interface KpiCardProps {
  label: string;
  value: number;
  /** Es. "%" per il completamento — apposto dopo il numero animato. */
  suffix?: string;
}

function KpiCard({ label, value, suffix = "" }: KpiCardProps) {
  const animated = useCountUp(value);
  return (
    <div className="rounded-card border border-line bg-white p-5 shadow-card transition-shadow duration-150 hover:shadow-lift">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">{label}</p>
      <p className="mt-2 text-3xl font-bold tabular-nums text-ink">
        {animated}
        {suffix}
      </p>
    </div>
  );
}

function ChartCard({
  title,
  children,
  interactive,
  height = 280,
}: {
  title: string;
  children: ReactNode;
  /** Aggiunge il cursore a mano + un suggerimento: il grafico è cliccabile (drill-down). */
  interactive?: boolean;
  /**
   * Altezza dell'area del grafico. Fissa a 280 per quasi tutti — torte e
   * istogrammi verticali non cambiano forma al crescere dei dati. Le barre
   * ORIZZONTALI sì: ogni categoria è una riga, e con abbastanza righe l'altezza
   * fissa le comprime fino a farle collidere. Vedi il grafico dei centri.
   */
  height?: number;
}) {
  return (
    <div className="rounded-card border border-line bg-white p-5 shadow-card transition-shadow duration-150 hover:shadow-lift">
      <div className="mb-4 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">{title}</h2>
        {interactive && <span className="text-xs font-normal normal-case text-ink/40">clicca per filtrare</span>}
      </div>
      <div style={{ height }} className={interactive ? "cursor-pointer" : undefined}>
        {children}
      </div>
    </div>
  );
}

/** Quanti caratteri stanno in `LARGHEZZA_ASSE_Y` px a 12px di font, in cifra prudente. */
const MAX_CARATTERI_TICK = 24;
const LARGHEZZA_ASSE_Y = 170;
/** Altezza minima per riga di un grafico a barre orizzontali, perché due etichette non si tocchino. */
const ALTEZZA_PER_CATEGORIA = 26;

/**
 * Etichetta dell'asse Y su UNA riga sola, troncata con l'ellissi e col nome
 * completo in un `<title>` (tooltip nativo al passaggio del mouse).
 *
 * Serve perché il comportamento predefinito di recharts su un'etichetta lunga è
 * mandarla a capo su più righe, e quelle righe finiscono addosso ai tick vicini:
 * con venti centri assistenza dai nomi reali si contavano sette coppie di
 * etichette sovrapposte, illeggibili. Il troncamento è lo stesso pattern che
 * l'app usa ovunque per il testo lungo (`truncate` di Tailwind) — solo che qui
 * siamo dentro un SVG, dove il CSS di troncamento non si applica e va fatto a
 * mano sulla stringa.
 *
 * Il nome intero non si perde: sta nel `<title>` e nel tooltip del grafico.
 */
function TickAsseY({
  x,
  y,
  payload,
}: {
  x?: number;
  y?: number;
  payload?: { value?: string };
}) {
  const testo = payload?.value ?? "";
  const troncato = testo.length > MAX_CARATTERI_TICK ? `${testo.slice(0, MAX_CARATTERI_TICK - 1)}…` : testo;

  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={12} fill="#6b7280">
      {troncato !== testo && <title>{testo}</title>}
      {troncato}
    </text>
  );
}

export default function Statistics() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<scheduleApi.ScheduleStatsFull | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Riepilogo dei quattro moduli: la panoramica trasversale. Resta distinto da
  // /schedule/stats/full, che copre le dimensioni proprie delle installazioni
  // (zona, tipo dispositivo, centro) e non ha senso generalizzare agli altri
  // moduli — i due endpoint sono complementari, non alternativi.
  const [riepilogo, setRiepilogo] = useState<dashboardApi.RiepilogoModuli | null>(null);
  const [riepilogoLoading, setRiepilogoLoading] = useState(true);

  // Drill-down (Fase 1.2): un click su una fetta/barra apre il Calendario
  // Installazioni filtrato per quella dimensione. Le Statistiche aggregano
  // installation_schedule, stessa tabella del Calendario → conteggi coerenti.
  // `name` può arrivare direttamente o dentro `payload` a seconda del tipo di grafico.
  function drill(key: "stato" | "zona" | "tipo" | "installatore", entry: { name?: string; payload?: { name?: string } }) {
    const value = entry?.name ?? entry?.payload?.name;
    if (value) navigate(`/schedule?${key}=${encodeURIComponent(value)}`);
  }

  // Drill-down per stato verso la lista del modulo. Esiste dove la pagina di
  // destinazione ha davvero il filtro: installazioni (?stato=) e interventi
  // (?stato=, con la convenzione __nulli__ per i record senza stato).
  // Sopralluoghi e attivazioni non hanno un filtro equivalente: i loro donut
  // restano informativi invece di offrire un click che non filtrerebbe nulla.
  function drillInstallazioni(stato: string) {
    navigate(`/schedule?stato=${encodeURIComponent(stato)}`);
  }
  function drillInterventi(stato: string) {
    const valore = stato === "Senza stato" ? interventionsApi.STATO_NULLI : stato;
    navigate(`/interventions?stato=${encodeURIComponent(valore)}`);
  }

  useEffect(() => {
    scheduleApi
      .getScheduleStatsFull()
      .then(setStats)
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare le statistiche."), "error"))
      .finally(() => setIsLoading(false));

    dashboardApi
      .getRiepilogo()
      .then(setRiepilogo)
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare il riepilogo dei moduli."), "error"))
      .finally(() => setRiepilogoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <p className="text-sm text-ink/40">Caricamento…</p>;
  }

  if (!stats) {
    return (
      <div className="rounded-card border border-dashed border-line bg-white/50 p-10 text-center text-sm text-ink/50">
        Impossibile mostrare le statistiche al momento.
      </div>
    );
  }

  const zonaData = toChartData(stats.by_zona);
  const installatoreData = toChartData(stats.by_installatore).sort((a, b) => b.value - a.value);
  const tipoData = toChartData(stats.by_tipo_dispositivo);

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-ink">Statistiche</h1>
        <p className="mt-1 text-sm text-ink/50">
          Andamento e distribuzione dei quattro moduli, con l'approfondimento sulle installazioni.
        </p>
      </header>

      {/* --- Panoramica dei quattro moduli ---------------------------------- */}
      <RiepilogoModuli dati={riepilogo} isLoading={riepilogoLoading} />

      {/* --- Andamento comparato -------------------------------------------- */}
      <section className="mb-8">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Andamento ultimi 12 mesi</h2>
        <div className="rounded-card border border-line bg-white p-5 shadow-card">
          <div style={{ height: 300 }}>
            {riepilogoLoading ? (
              <p className="text-sm text-ink/40">Caricamento…</p>
            ) : riepilogo ? (
              <AndamentoModuli dati={riepilogo} />
            ) : (
              <EmptyChart />
            )}
          </div>
          <p className="mt-3 text-xs text-ink/40">
            Ogni modulo è contato sulla propria data di riferimento, la stessa usata dal calendario. I record senza
            data non compaiono nel grafico: il loro numero è indicato nelle card qui sopra.
          </p>
        </div>
      </section>

      {/* --- Distribuzione per stato, modulo per modulo ---------------------
          Gli stati NON sono comparabili fra moduli: enumerazioni diverse, e gli
          Interventi ricevono valori liberi da Odoo. Quindi quattro donut con la
          propria legenda, non un grafico unico. */}
      <section className="mb-8">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Distribuzione per stato</h2>
        {riepilogoLoading ? (
          <p className="text-sm text-ink/40">Caricamento…</p>
        ) : riepilogo ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <DonutPerStato
              titolo="Installazioni"
              perStato={riepilogo.installazioni.per_stato}
              ordine={STATO_ORDER}
              onSelezionaStato={drillInstallazioni}
            />
            <DonutPerStato titolo="Sopralluoghi" perStato={riepilogo.sopralluoghi.per_stato} />
            <DonutPerStato
              titolo="Interventi"
              perStato={riepilogo.interventi.per_stato}
              onSelezionaStato={drillInterventi}
            />
            <DonutPerStato titolo="Attivazioni" perStato={riepilogo.attivazioni.per_stato} />
          </div>
        ) : (
          <EmptyChart />
        )}
      </section>

      {/* --- Approfondimento installazioni ----------------------------------
          Zona, tipo dispositivo e centro assistenza sono dimensioni proprie
          delle installazioni: non esistono sugli altri moduli, quindi restano
          una sezione a sé alimentata da /schedule/stats/full. */}
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Approfondimento installazioni</h2>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard label="Totale installazioni" value={stats.totale} />
        <KpiCard label="Previste questo mese" value={stats.mese_corrente} />
        <KpiCard label="% Completamento" value={stats.percentuale_completamento} suffix="%" />
      </div>

      {/* --- Grafici ----------------------------------------------------------
          Niente donut per stato qui: le installazioni per stato stanno già
          nella sezione dei quattro moduli, con lo stesso drill-down. Restano
          le dimensioni che esistono solo su questo modulo. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Distribuzione per tipo dispositivo" interactive>
          {tipoData.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tipoData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={90}
                  paddingAngle={3}
                  cornerRadius={4}
                  label
                  onClick={(entry) => drill("tipo", entry)}
                >
                  {tipoData.map((entry, i) => (
                    <Cell key={entry.name} fill={TIPO_COLORS[i % TIPO_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Distribuzione per zona" interactive>
          {zonaData.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={zonaData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#18273d" radius={[4, 4, 0, 0]} onClick={(entry) => drill("zona", entry)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* L'altezza cresce col numero di centri invece di restare fissa: qui
            ogni categoria è una RIGA, e comprimerne venti in 280px le fa
            collidere. Il minimo resta 280 così con pochi centri la card non si
            accartoccia e resta allineata a quella accanto. */}
        <ChartCard
          title="Distribuzione per centro assistenza"
          interactive
          height={Math.max(280, installatoreData.length * ALTEZZA_PER_CATEGORIA)}
        >
          {installatoreData.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={installatoreData} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                {/* `interval={0}` forza un'etichetta per barra: il valore
                    predefinito ne salta a piacere quando lo spazio è poco, e con
                    venti centri ne spariva la metà — barre senza nome, che è
                    peggio di un nome troncato. */}
                <YAxis
                  type="category"
                  dataKey="name"
                  width={LARGHEZZA_ASSE_Y}
                  interval={0}
                  tick={<TickAsseY />}
                />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill="#f3b149"
                  radius={[0, 4, 4, 0]}
                  onClick={(entry) => drill("installatore", entry)}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* --- Mappa Wink On Cash ---------------------------------------------
          In fondo e non in cima: è un approfondimento su un servizio, non una
          sintesi della pagina. Nascosta a chi non ha `devices:read` — questa
          pagina è protetta da `statistiche:read`, che è un permesso diverso e
          non implica l'accesso al parco device. */}
      {hasPermission("devices:read") && <MappaWinkOnCash />}
    </div>
  );
}

function EmptyChart() {
  return <div className="flex h-full items-center justify-center text-sm text-ink/40">Nessun dato disponibile.</div>;
}
